import React, { useState } from 'react';
import { ArrowRight, ExternalLink, Tag } from 'lucide-react';
import { partnersData } from '../partnersData';
import AnimatedSection from '../components/AnimatedSection';
import SectionHeading from '../components/SectionHeading';
import SEO from '../components/SEO';
import { useIntersectionObserver } from '../hooks/useIntersectionObserver';
import { useI18n } from '../i18n/I18nContext';

function PartnerLogo({ partner }: { partner: typeof partnersData[0] }) {
  const [imgError, setImgError] = useState(false);

  if (partner.logoUrl && !imgError) {
    return (
      <img
        src={partner.logoUrl}
        alt={`${partner.name} logo`}
        className="max-h-10 max-w-full object-contain"
        onError={() => setImgError(true)}
      />
    );
  }

  if (partner.logoWordmark) {
    const { text, colors } = partner.logoWordmark;
    const letters = text.split('');
    return (
      <span className="font-bold text-lg tracking-tight leading-none select-none">
        {letters.map((ch, i) =>
          ch === ' ' ? (
            <span key={i}>&nbsp;</span>
          ) : (
            <span key={i} style={{ color: colors[i % colors.length] }}>
              {ch}
            </span>
          )
        )}
      </span>
    );
  }

  return <span className="text-xl font-bold text-white">{partner.name}</span>;
}

function PartnerCard({ partner, index }: { partner: typeof partnersData[0]; index: number }) {
  const { t } = useI18n();
  const [isVisible, setIsVisible] = useState(false);

  const ref = useIntersectionObserver({
    onIntersect: () => setIsVisible(true),
    threshold: 0.05,
    rootMargin: '0px 0px -20px 0px',
  });

  return (
    <div
      ref={ref as React.RefObject<HTMLDivElement>}
      style={{ transitionDelay: `${index * 100}ms` }}
      className={`bg-zinc-900 border border-zinc-800 rounded-xl p-8 flex flex-col transition-all duration-700 ease-out hover:shadow-xl hover:-translate-y-1 hover:border-zinc-600 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
      }`}
    >
      <div className="flex items-start justify-between mb-6">
        <div className="h-14 w-44 flex items-center">
          <PartnerLogo partner={partner} />
        </div>
        <span className="text-xs font-semibold uppercase tracking-wider text-[#FDCB12] bg-[#FDCB12]/10 border border-[#FDCB12]/20 px-3 py-1 rounded-full flex-shrink-0 ml-2">
          {partner.category}
        </span>
      </div>
      <h3 className="text-xl font-bold text-white mb-3">{partner.name}</h3>
      <p className="text-gray-400 leading-relaxed flex-1 mb-4">{partner.description}</p>
      {partner.deal && (
        <div className="flex items-center gap-2 bg-[#FDCB12]/10 border border-[#FDCB12]/30 rounded-lg px-4 py-2.5 mb-5">
          <Tag className="h-3.5 w-3.5 text-[#FDCB12] flex-shrink-0" />
          <span className="text-[#FDCB12] text-sm font-semibold">{partner.deal}</span>
        </div>
      )}
      <a
        href={partner.referralUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center justify-center gap-2 bg-[#FDCB12] text-black font-semibold px-6 py-3 rounded-md hover:bg-[#FF5500] hover:text-white transition-all duration-300 transform hover:scale-105"
      >
        {t.partners.getStarted} <ExternalLink className="h-4 w-4" />
      </a>
    </div>
  );
}

function PartnersPage() {
  const { t, localizedPath } = useI18n();

  return (
    <>
      <SEO
        title={t.seo.partnersTitle}
        description={t.seo.partnersDescription}
        canonicalPath={localizedPath('/partners')}
      />

      {/* Hero */}
      <section className="relative py-24 px-4">
        <div className="container mx-auto px-4 max-w-4xl text-center">
          <SectionHeading eyebrow={t.partners.eyebrow} title={t.partners.title} description={t.partners.description} />
        </div>
        <div className="absolute right-0 bottom-0 w-1/3 h-full opacity-10 bg-gradient-to-l from-[#FDCB12]" />
      </section>

      {/* Partners Grid */}
      <section className="py-16 bg-surface-1">
        <div className="container mx-auto px-4">
          <div className="stagger-grid grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {partnersData.map((partner, i) => (
              <PartnerCard key={partner.name} partner={partner} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-base">
        <div className="container mx-auto px-4">
          <AnimatedSection animation="fade-in-up">
            <div className="max-w-2xl mx-auto text-center bg-zinc-900 border border-zinc-800 rounded-xl p-12">
              <h2 className="text-3xl font-bold text-white mb-4">
                {t.partners.ctaTitle}
              </h2>
              <p className="text-gray-400 mb-8">
                {t.partners.ctaText}
              </p>
              <a
                href={`/#contact`}
                className="inline-flex items-center gap-2 bg-[#FDCB12] text-black font-semibold px-8 py-4 rounded-md hover:bg-[#FF5500] hover:text-white transition-all duration-300 transform hover:scale-105"
              >
                {t.partners.ctaButton} <ArrowRight className="h-5 w-5" />
              </a>
            </div>
          </AnimatedSection>
        </div>
      </section>

      <p className="text-center text-xs text-gray-600 pb-8 px-4">
        {t.partners.disclaimer}
      </p>
    </>
  );
}

export default PartnersPage;
